import React, { useCallback, useState } from 'react';
import { Card, SectionHeader } from '../../components/ui';
import type { AiBudget } from '../../types/aiOps';
import { isAdminRole } from '../intelligence/components/BriefPanel';
import { BudgetSection } from './components/BudgetSection';
import { ProviderStatusSection } from './components/ProviderStatus';
import { SpendSection } from './components/SpendSection';
import { Notice } from './components/ui';
import { currentMonth } from './format';
import { useBudget, useProviderHealth, useUsage } from './useAiOps';

/**
 * What the AI is doing, what it costs and what it may spend — the three things
 * an admin needs before trusting anything else on this page. Admin-only, and
 * small enough to sit under the provider keys rather than on a page of its own.
 */
export const AiOpsSections: React.FC<{ className?: string }> = ({ className = '' }) => {
  const admin = isAdminRole();
  const [month, setMonth] = useState<string>(currentMonth());
  const [usageToken, setUsageToken] = useState(0);

  const health = useProviderHealth(admin);
  const usage = useUsage(month, admin, usageToken);
  const budget = useBudget(admin);

  // A check makes real calls, so the month's spend moves with it
  const onCheck = useCallback(async () => {
    const ok = await health.check();
    if (ok) setUsageToken((token) => token + 1);
  }, [health]);

  const onSaveBudget = useCallback(
    async (next: AiBudget) => {
      const ok = await budget.save(next);
      if (ok) setUsageToken((token) => token + 1);
    },
    [budget],
  );

  // These numbers name provider keys and what the organisation is spending
  if (!admin) return null;

  const unavailable = health.unavailable || usage.unavailable || budget.unavailable;

  if (unavailable) {
    return (
      <section className={className}>
        <SectionHeader title="Provider status" />
        <Card className="p-3">
          <Notice tone="quiet">
            Provider health and spend are not available on this server yet — redeploy the backend to see them.
          </Notice>
        </Card>
      </section>
    );
  }

  return (
    <div className={className}>
      <ProviderStatusSection
        report={health.report}
        loading={health.loading}
        checking={health.checking}
        error={health.error}
        onCheck={onCheck}
      />
      <SpendSection
        className="mt-8"
        usage={usage.usage}
        month={month}
        loading={usage.loading}
        error={usage.error}
        onMonthChange={setMonth}
      />
      <BudgetSection
        className="mt-8"
        budget={budget.budget}
        loading={budget.loading}
        saving={budget.saving}
        error={budget.error}
        onSave={onSaveBudget}
      />
    </div>
  );
};
